import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { loginUser } from "../api/users";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import {
  getSafeReturnPath,
  rememberReturnUrl,
} from "../utils/authReturn";

const LoginPage = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const returnPath = getSafeReturnPath(
    location.state?.from
      ? `${location.state.from.pathname || ""}${location.state.from.search || ""}`
      : null,
    "/"
  );

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await loginUser(formData);
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(err.message || t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    rememberReturnUrl(returnPath);
    window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/users/google/login/`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "80vh",
      }}
    >
      <Box
        sx={{
          maxWidth: 400,
          width: "100%",
          p: 4,
          mt: 8,
          boxShadow: 3,
          borderRadius: 2,
          textAlign: "center",
          backgroundColor: "white",
        }}
      >
        <Typography variant="h4" fontWeight="bold" mb={3}>
          {t("auth.loginTitle")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            name="username"
            type="email"
            fullWidth
            required
            variant="outlined"
            value={formData.username}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t("auth.password")}
            name="password"
            type="password"
            fullWidth
            required
            variant="outlined"
            value={formData.password}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <Button
            onClick={() => navigate("/forgot-password")}
            sx={{ mb: 2, textTransform: "none", float: "right", fontSize: "0.85rem" }}
          >
            {t("auth.forgotPassword")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={loading}
          >
            {loading ? t("auth.loggingIn") : t("auth.login")}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>{t("auth.or")}</Divider>

        <Button
          onClick={handleGoogleLogin}
          startIcon={<GoogleIcon />}
          variant="outlined"
          fullWidth
          sx={{ textTransform: "none" }}
        >
          {t("auth.loginWithGoogle")}
        </Button>

        <Button
          onClick={() => navigate("/registration")}
          sx={{ mt: 2, textTransform: "none" }}
        >
          {t("auth.noAccount")}
        </Button>
      </Box>
    </Box>
  );
};

export default LoginPage;
